/**
 * A section (chapter) within the spine.
 */
export interface SectionItem {
  idref: string;
  linear: string;
  properties: string[];
  index: number;
  href: string;
  url: string;
  canonical: string;
  cfiBase: string;
}

export class Section {
  readonly idref: string;
  readonly linear: boolean;
  readonly properties: string[];
  readonly index: number;
  readonly href: string;
  readonly url: string;
  readonly canonical: string;
  readonly cfiBase: string;

  document: Document | undefined;
  contents: Element | undefined;
  output: string | undefined;

  private _prev: (() => Section | null) | undefined;
  private _next: (() => Section | null) | undefined;

  constructor(item: SectionItem) {
    this.idref = item.idref;
    this.linear = item.linear !== "no";
    this.properties = item.properties;
    this.index = item.index;
    this.href = item.href;
    this.url = item.url;
    this.canonical = item.canonical;
    this.cfiBase = item.cfiBase;
  }

  setPrev(fn: () => Section | null): void {
    this._prev = fn;
  }
  setNext(fn: () => Section | null): void {
    this._next = fn;
  }

  prev(): Section | null {
    return this._prev?.() ?? null;
  }
  next(): Section | null {
    return this._next?.() ?? null;
  }

  /**
   * Load section content from archive or request function.
   * @param requestFn Load function — receives href, returns Document
   */
  async load(requestFn: (url: string) => Promise<Document>): Promise<Element> {
    if (this.contents) return this.contents;
    const doc = await requestFn(this.url);
    this.document = doc;
    this.contents = doc.documentElement;
    return this.contents;
  }

  /**
   * Render section to serialized XHTML string.
   */
  async render(requestFn: (url: string) => Promise<Document>): Promise<string> {
    await this.load(requestFn);
    const serializer = new XMLSerializer();
    this.output = serializer.serializeToString(this.contents!);
    return this.output;
  }

  unload(): void {
    this.document = undefined;
    this.contents = undefined;
    this.output = undefined;
  }

  destroy(): void {
    this.unload();
    this._prev = undefined;
    this._next = undefined;
  }
}
